import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, FormInputText, Link } from '@hopskipdrive/react-components';
import { Email, LockRounded } from '@mui/icons-material';
import { HandleLogin } from '@rideiq/auth/types';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import { AnyObjectSchema } from 'yup';

import { ContainerForBanner, StyledAction } from './styles';

export interface LoginFormProps {
  handleSignIn: HandleLogin;
  handleForgotPassword: () => void;
  validationSchema?: AnyObjectSchema;
  serverErrors?: string[];
}

type FormData = {
  email: string;
  password: string;
};

export const LoginForm: FC<LoginFormProps> = ({
  handleSignIn,
  handleForgotPassword,
  validationSchema,
  serverErrors,
}) => {
  const {
    control,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<FormData>({
    ...(validationSchema && { resolver: yupResolver(validationSchema) }),
    shouldUnregister: true,
    defaultValues: {
      password: '',
    },
  });

  const submit = (data, e) => {
    e.preventDefault();
    handleSignIn(data, resetField);
  };

  return (
    <ContainerForBanner>
      {serverErrors && (
        <Box>
          {serverErrors.map((error) => (
            <Alert key={error} severity="error" message={error} icon={false} />
          ))}
        </Box>
      )}
      <PageHeading variant="h1">Sign in</PageHeading>
      <form className="form" onSubmit={handleSubmit(submit)}>
        <FormA11yErrorAnnouncer errors={errors} />
        <Controller
          name="email"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormInputText
              aria-label="Email Address"
              id="login-email"
              inputProps={{
                'data-testid': 'email',
                autocomplete: 'email',
              }}
              placeholder="Email Address"
              type="email"
              variant="outlined"
              icon={<Email />}
              error={!!errors.email || !!serverErrors?.length}
              autoFocus
              helperText={errors.email ? errors.email.message : ''}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <FormInputText
                aria-label="Password"
                id="login-password"
                inputProps={{
                  'data-testid': 'password',
                  autocomplete: 'current-password',
                }}
                placeholder="Password"
                type="password"
                variant="outlined"
                icon={<LockRounded />}
                error={!!errors.password || !!serverErrors?.length}
                helperText={errors.password ? errors.password.message : ''}
                value={value}
                onChange={onChange}
              />
            );
          }}
        />

        <StyledAction className="form-actions">
          <Button type="submit" color="primary" data-testid="submitButton">
            Sign In
          </Button>
          <Link component="button" onClick={handleForgotPassword}>
            Forgot password
          </Link>
        </StyledAction>
      </form>
    </ContainerForBanner>
  );
};
